import { civilFromEpochDay, epochDayFromCivil, isLeapYear } from './calendar';
import { monthBlend, type MonthBlend } from './waterColumn';

/**
 * A place's observed record of sea surface temperature against its normal
 * (M24): how far each day or month was above or below the 1991 to 2020 normal
 * for its date, as the record measured it. The game's normal is the World
 * Ocean Atlas's (`waterColumn.ts`); a departure from a record is added to it.
 *
 * Two kinds: the aquarium's, daily, from NOAA's OISST v2.1 over its square,
 * with the marine heatwave threshold (`data/climate/oisstMauiNui.ts`); and each
 * region's, monthly, from NOAA's ERSST v5 (`data/climate/ersstRegions.ts`),
 * read by the day between the middles of two months as the water is
 * (`monthBlend`).
 */
export interface DailySst {
  source: string;
  /** The first and last day of `anomalyCentiC`, as days since 1 January 1970. */
  firstEpochDay: number;
  lastEpochDay: number;
  /** Each day's departure from its normal, hundredths of a °C. */
  anomalyCentiC: readonly number[];
  /** The marine heatwave threshold's height above the normal, by day of a 366-day year (`dayOf366`), hundredths of a °C. */
  thresholdCentiC: readonly number[];
  /** Coral Reef Watch's Maximum Monthly Mean's height above the normal, by day of a 366-day year, hundredths of a °C (M46, `heatStress.ts`). */
  mmmAboveNormalCentiC: readonly number[];
  /**
   * The Maximum Monthly Mean itself, °C, on the record's own scale: what its
   * HotSpots are measured from. The game's temperature is the Atlas's normal
   * plus the departure, so nothing in the game reads this; the checks do
   * (`scripts/reefHeat.ts`).
   */
  mmmC: number;
  /** The calendar month the Maximum Monthly Mean is, 1 to 12: the record's warmest (the checks read it, `scripts/data/reefWater.ts`). */
  mmmMonth: number;
  /** The departures' least-squares trend over the record, °C a year. */
  trendCPerYear: number;
  /** The lag at which the departures' correlation falls to 1/e, days. */
  decorrelationDays: number;
}

export interface MonthlySst {
  source: string;
  /** The months of the first and last `anomalyCentiC`. */
  first: { readonly year: number; readonly month: number };
  last: { readonly year: number; readonly month: number };
  /** Each month's departure from its normal, hundredths of a °C. */
  anomalyCentiC: readonly number[];
  trendCPerYear: number;
  decorrelationDays: number;
}

export type SstRecord = ({ kind: 'daily' } & DailySst) | ({ kind: 'monthly' } & MonthlySst);

/** The tables' unit, hundredths of a °C, and the months of a year (definitions). */
const CENTI = 100;
const MONTHS_A_YEAR = 12;

/** A table's value in °C. */
export function fromCenti(centi: number): number {
  return centi / CENTI;
}

/** A month's departure, °C, or null outside the record. */
function monthC(record: MonthlySst, year: number, month: number): number | null {
  const i = (year - record.first.year) * MONTHS_A_YEAR + (month - record.first.month);
  const lastIndex = (record.last.year - record.first.year) * MONTHS_A_YEAR + (record.last.month - record.first.month);
  return i >= 0 && i <= lastIndex ? fromCenti(record.anomalyCentiC[i] as number) : null;
}

/**
 * The last few days' blends of two months: the twelve regions read the same
 * day, so each day's is found once, not twelve times. MEASURED (M24, `npm run
 * sim -- --years 10 --seed 42`, six runs): the tick 17.8 µs with the blend
 * found every time, 9.4 to 10.1 µs with it kept. (Keeping the date years
 * earlier too saved 0.6 µs more, not worth its code.)
 */
const blends = new Map<number, MonthBlend>();
const KEPT_BLENDS = 8;

function blendOn(epochDay: number): MonthBlend {
  let blend = blends.get(epochDay);
  if (!blend) {
    blend = monthBlend(epochDay);
    if (blends.size >= KEPT_BLENDS) blends.clear();
    blends.set(epochDay, blend);
  }
  return blend;
}

/** The record's departure on a day, °C, or null where the record does not reach. */
export function recordedC(record: SstRecord, epochDay: number): number | null {
  if (record.kind === 'daily') {
    return epochDay >= record.firstEpochDay && epochDay <= record.lastEpochDay
      ? fromCenti(record.anomalyCentiC[epochDay - record.firstEpochDay] as number)
      : null;
  }
  const p = blendOn(epochDay);
  const a = monthC(record, p.fromYear, p.from + 1);
  const b = monthC(record, p.toYear, p.to + 1);
  return a === null || b === null ? null : a + (b - a) * p.toward;
}

/** 29 February, and the day it is read as in a year without one, 28 February (definitions). */
const FEBRUARY = 2;
const LEAP_DAY = 29;
const LAST_COMMON_DAY = 28;

/** The same date `years` years earlier, 29 February being 28 February in a year without one. */
function yearsEarlier(epochDay: number, years: number): number {
  if (years === 0) return epochDay;
  const { year, month, day } = civilFromEpochDay(epochDay);
  const target = year - years;
  return epochDayFromCivil(target, month, month === FEBRUARY && day === LEAP_DAY && !isLeapYear(target) ? LAST_COMMON_DAY : day);
}

/**
 * A place's departure on a day as the record has it `shiftYears` earlier,
 * moved along the record's trend by as many years: the record itself when the
 * shift is 0, an analog year's when it is not (`systems/climate.ts`).
 */
export function shiftedC(record: SstRecord, epochDay: number, shiftYears: number): number {
  const v = recordedC(record, yearsEarlier(epochDay, shiftYears));
  // A RangeError, so a save that asks for a day the record does not have is called damaged (`save.ts`).
  if (v === null) throw new RangeError(`the ${record.source} record has no day ${epochDay} shifted ${shiftYears} years`);
  return v + record.trendCPerYear * shiftYears;
}
